/**
 * FO-032 (batch-16) + batch-63 werkbon-facturatie (FO-474) — WAAROM een werkbon
 * niet gefactureerd wordt.
 *
 * Bewust een BLAD-module (alleen `zod`): `werkbonnen.ts`, `facturen.ts` en
 * `werkbon-facturatie.ts` lezen deze waarden alle drie op runtime, en
 * `facturen.ts` her-exporteert al uit `werkbonnen.ts`. Zouden de redenen in één
 * van die drie leven, dan ontstaat er een runtime-importcyclus (TDZ bij de
 * `z.enum(...)` hieronder). De barrel krijgt ze via `facturen.ts`.
 *
 * De regel van FO-474: `facturabel: false` bestaat nooit zonder reden. Een
 * vaste lijst waar het kan (Prisma-enum `WerkbonNietFacturabelReden`), vrije
 * tekst waar het moet (OVERIG vereist een toelichting), maar nooit leeg.
 */
import { z } from 'zod'

export const WERKBON_NIET_FACTURABEL_REDEN = [
  'INTERN',
  'GARANTIE',
  'DUBBEL',
  'COULANCE',
  'OVERIG',
  // batch-63 (FO-474) — systeemreden: de werkbon is aangemaakt terwijl de klant
  // op "werkbonnen standaard niet facturabel" stond.
  'KLANT_INSTELLING',
] as const
export type WerkbonNietFacturabelReden = (typeof WERKBON_NIET_FACTURABEL_REDEN)[number]

export const WERKBON_NIET_FACTURABEL_REDEN_LABELS: Record<WerkbonNietFacturabelReden, string> = {
  INTERN: 'Intern werk',
  GARANTIE: 'Garantie',
  DUBBEL: 'Dubbel',
  COULANCE: 'Coulance',
  OVERIG: 'Overig',
  KLANT_INSTELLING: 'Klant standaard niet facturabel',
}

/**
 * De redenen die een gebruiker zelf kiest. KLANT_INSTELLING is een systeemreden
 * (gezet door de klant-default bij het aanmaken) en staat daarom niet in
 * keuzelijsten; de API weigert hem op handmatige paden.
 */
export const WERKBON_NIET_FACTURABEL_REDEN_HANDMATIG = [
  'INTERN',
  'GARANTIE',
  'DUBBEL',
  'COULANCE',
  'OVERIG',
] as const satisfies readonly WerkbonNietFacturabelReden[]
export type WerkbonNietFacturabelRedenHandmatig =
  (typeof WERKBON_NIET_FACTURABEL_REDEN_HANDMATIG)[number]

/**
 * De redenen die een FORMULIER aanbiedt (aanmaken, bulk, bewerken, mobiel): de
 * handmatige zonder OVERIG. "Overig" zegt zonder toelichting niets, en alleen
 * "Uit facturatie halen" op de facturatie-workflow (FO-032) legt die
 * toelichting vast (verplicht, in de audit). Zo bestaat "Niet te factureren
 * (overig)" nooit zonder uitleg.
 */
export const WERKBON_NIET_FACTURABEL_REDEN_FORMULIER = [
  'INTERN',
  'GARANTIE',
  'DUBBEL',
  'COULANCE',
] as const satisfies readonly WerkbonNietFacturabelRedenHandmatig[]
export type WerkbonNietFacturabelRedenFormulier =
  (typeof WERKBON_NIET_FACTURABEL_REDEN_FORMULIER)[number]

export function isFormulierReden(
  v: string | null | undefined,
): v is WerkbonNietFacturabelRedenFormulier {
  return (WERKBON_NIET_FACTURABEL_REDEN_FORMULIER as readonly string[]).includes(v ?? '')
}

/** Bij OVERIG is een toelichting verplicht: nooit een lege reden. */
export const FACTURABEL_TOELICHTING_MIN = 3
export const FACTURABEL_TOELICHTING_MAX = 500

/**
 * Basis van het "niet te factureren"-label. Gelijk aan
 * `WERKBON_FACTURATIE_STATUS_LABELS.NIET_FACTURABEL` (een unit-test bewaakt dat);
 * hier als eigen literal zodat deze module een blad blijft.
 */
export const NIET_FACTURABEL_BASIS_LABEL = 'Niet te factureren'

/** Label van een niet-facturabele bon zonder vastgelegde reden: een omgevallen vlag. */
export const NIET_FACTURABEL_ZONDER_REDEN_LABEL = `${NIET_FACTURABEL_BASIS_LABEL} (zonder reden - controleren)`

/**
 * Het label van een niet-facturabele bon: altijd mét reden erachter, zodat
 * lijst, planbord en detail hetzelfde verhaal vertellen. Zonder reden: het
 * controle-label.
 */
export function nietFacturabelLabel(reden: WerkbonNietFacturabelReden | null): string {
  if (!reden) return NIET_FACTURABEL_ZONDER_REDEN_LABEL
  return `${NIET_FACTURABEL_BASIS_LABEL} (${WERKBON_NIET_FACTURABEL_REDEN_LABELS[reden].toLowerCase()})`
}

/** Melding als een schrijfpad `facturabel: false` zonder reden aanbiedt. */
export const FACTURABEL_REDEN_VERPLICHT_MELDING =
  'Kies een reden: een werkbon gaat nooit zonder reden uit de facturatie.'

/** Veldfout als een formulier "Overig" nieuw kiest (dat kan alleen mét toelichting). */
export const FACTURABEL_REDEN_OVERIG_MELDING =
  '"Overig" vraagt een toelichting: kies een andere reden, of gebruik "Uit facturatie halen" op de facturatie-workflow.'

/** Melding als er een reden meekomt terwijl de werkbon niet op "niet facturabel" gaat. */
export const FACTURABEL_REDEN_ZONDER_UIT_MELDING = 'Een reden hoort alleen bij "niet facturabel".'

/**
 * De redenen die een gebruiker op een schrijfpad mag kiezen (zonder de
 * systeemreden KLANT_INSTELLING). Gedeeld door de create-, bulk- en
 * update-schema's en de formulieren.
 */
export const facturabelRedenInvoerSchema = z.enum(WERKBON_NIET_FACTURABEL_REDEN_HANDMATIG)

/**
 * De redenen van een formulier (aanmaken, bulk, mobiel): zonder OVERIG en zonder
 * de systeemreden; een andere waarde geeft een leesbare veldfout.
 */
export const facturabelRedenFormulierSchema = z.enum(WERKBON_NIET_FACTURABEL_REDEN_FORMULIER, {
  error: 'Kies een van de redenen uit de lijst ("Overig" loopt via de facturatie-workflow).',
})

/**
 * Alle redenen, incl. de systeemreden. Voor het PATCH-schema van de werkbon: dat
 * moet zijn eigen read-DTO terug accepteren (CLAUDE.md read<->write), ook een bon
 * die al op KLANT_INSTELLING staat. Het NIEUW kiezen van die systeemreden weigert
 * de route zelf (veldfout op `facturabelReden`).
 */
export const facturabelRedenSchema = z.enum(WERKBON_NIET_FACTURABEL_REDEN)

/** Veldfout als een gebruiker de systeemreden zelf probeert te kiezen. */
export const FACTURABEL_REDEN_SYSTEEM_MELDING =
  'Deze reden zet het systeem zelf (klant standaard niet facturabel); kies een andere reden.'

/**
 * De gedeelde regel voor elk schrijfpad met een facturabel-keuze: een werkbon
 * gaat alleen mét reden op `facturabel: false`, en een reden hoort alleen bij
 * `facturabel: false`. Gebruik in een `.superRefine` (API) of vóór verzenden
 * (formulier); de veldfout landt inline op `facturabelReden`.
 */
export function controleerFacturabelKeuze(
  waarde: {
    facturabel?: boolean | null
    facturabelReden?: WerkbonNietFacturabelReden | null
  },
  ctx: z.RefinementCtx,
): void {
  if (waarde.facturabel === false && !waarde.facturabelReden) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['facturabelReden'],
      message: FACTURABEL_REDEN_VERPLICHT_MELDING,
    })
  }
  if (waarde.facturabelReden && waarde.facturabel !== false) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['facturabelReden'],
      message: FACTURABEL_REDEN_ZONDER_UIT_MELDING,
    })
  }
}

/**
 * De facturabel-keuze van een aanmaakformulier (alleen de formulier-redenen),
 * voor client-side validatie vóór verzenden: dezelfde regel als de API, zodat
 * het formulier niets kan sturen dat de API weigert.
 */
export const facturabelKeuzeInvoerSchema = z
  .object({
    facturabel: z.boolean().optional(),
    facturabelReden: facturabelRedenFormulierSchema.nullable().optional(),
  })
  .superRefine(controleerFacturabelKeuze)
export type FacturabelKeuzeInvoer = z.infer<typeof facturabelKeuzeInvoerSchema>
